import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
// @ts-ignore
import { getServicesForCustomer } from "../../services/serviceService";
// @ts-ignore
import { getHeroForCustomer } from "../../services/heroService";
import CTASection from "../../components/customer/CTASection";
import { useFooter } from "../../contexts/FooterContext";
// @ts-ignore
import { useAutoSEO } from '../../hooks/useSEOFromRoute';

export default function SolutionsPage() {
  // Auto fetch SEO from database based on route
  useAutoSEO();

  const [isVisible, setIsVisible] = useState(false);
  const [visibleSections, setVisibleSections] = useState({
    hero: false,
    solutions: false,
    services: false,
    cta: false,
  });
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [solutions, setSolutions] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [heroSection, setHeroSection] = useState<any>(null);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());

  const heroRef = useRef<HTMLElement>(null);
  const solutionsRef = useRef<HTMLElement>(null);
  const servicesRef = useRef<HTMLElement>(null);
  const ctaRef = useRef<HTMLElement>(null);
  const { footerData } = useFooter();

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  // Fetch hero section, solutions and services from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const [heroResponse, servicesResponse] = await Promise.all([
          getHeroForCustomer('services'),  // API: /api/heroes/customer/services
          getServicesForCustomer()
        ]);

        // Load hero section
        if (heroResponse?.success && heroResponse?.data) {
          const heroData = {
            ...heroResponse.data,
            isActive: heroResponse.data.isActive !== undefined ? heroResponse.data.isActive : true,
            buttons: heroResponse.data.buttons || []
          };
          setHeroSection(heroData);
        }
        
        if (servicesResponse?.data?.services) {
          const allServices = servicesResponse.data.services;
          
          // Filter solutions (type: 'solution')
          const solutionsList = allServices
            .filter((s: any) => s.type === 'solution')
            .map((s: any) => ({
              _id: s._id,
              title: s.name,
              subtitle: s.subtitle || '',
              description: s.shortDescription || '',
              fullDescription: s.description || '',
              icon: s.icon || 'ri-lightbulb-line',
              gradient: 'from-[#2a4b7c] via-[#1e3a5f] to-[#00c897]',
              link: `/giai-phap/${s.slug}`,
              features: s.features || [],
              image: s.featuredImage || '/placeholder-solution.jpg'
            }));
          
          // Filter services (type: 'service')
          const servicesList = allServices
            .filter((s: any) => s.type === 'service')
            .map((s: any, index: number) => ({
              _id: s._id,
              title: s.name,
              subtitle: s.subtitle || '',
              description: s.shortDescription || s.description || '',
              icon: s.icon || 'ri-service-line',
              link: `/dich-vu/${s.slug}`,
              color: index % 2 === 0 ? 'from-[#2a4b7c] to-[#1e3a5f]' : 'from-[#00c897] to-[#00b587]',
              features: s.features?.slice(0, 4) || [],
              highlight: s.description || ''
            }));
          
          setSolutions(solutionsList);
          setServices(servicesList);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Mouse tracking for 3D effects
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setMousePosition({ x, y });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Scroll animation observer
  useEffect(() => {
    const observerOptions = {
      threshold: 0.15,
      rootMargin: "0px 0px -80px 0px",
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionName = entry.target.getAttribute("data-section");
          if (sectionName) {
            setVisibleSections((prev) => ({ ...prev, [sectionName]: true }));
          }
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions
    );

    const refs = [
      { ref: heroRef, name: "hero" },
      { ref: solutionsRef, name: "solutions" },
      { ref: servicesRef, name: "services" },
      { ref: ctaRef, name: "cta" },
    ];

    refs.forEach(({ ref }) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      refs.forEach(({ ref }) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  // Handle image load
  const handleImageLoad = (index: number) => {
    setLoadedImages((prev) => new Set(prev).add(index));
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Video Background */}
      <section
        ref={heroRef}
        data-section="hero"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Image/Video */}
        <div className="absolute inset-0 w-full h-full">
          {heroSection?.backgroundImage ? (
            <img
              src={heroSection.backgroundImage}
              alt="Hero Background"
              className="w-full h-full object-cover transition-opacity duration-1000 opacity-0"
              onLoad={(e) => {
                e.currentTarget.classList.remove("opacity-0");
                e.currentTarget.classList.add("opacity-100");
              }}
            />
          ) : (
            <video
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover"
            >
              <source
                src="https://cdn.pixabay.com/video/2020/06/12/41980-431090090_large.mp4"
                type="video/mp4"
              />
            </video>
          )}

          <div className="absolute inset-0 bg-gradient-to-br from-[#2a4b7c]/95 via-[#1e3a5f]/90 to-[#00c897]/85"></div>
        </div>

        {/* 3D Floating Elements */}
        <div
          className="absolute inset-0 overflow-hidden pointer-events-none"
          style={{ perspective: "1000px" }}
        >
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute w-3 h-3 bg-white/20 rounded-full backdrop-blur-sm"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                transform: `translateZ(${
                  Math.random() * 200 - 100
                }px) rotateX(${mousePosition.y * 20}deg) rotateY(${
                  mousePosition.x * 20
                }deg)`,
                animation: `float-3d ${
                  3 + Math.random() * 4
                }s infinite ease-in-out`,
                animationDelay: `${Math.random() * 2}s`,
                transition: "transform 0.3s ease-out",
              }}
            ></div>
          ))}
        </div>

        {/* Content with 3D Transform */}
        <div
          className="relative z-10 max-w-7xl mx-auto px-6 py-32 text-center"
          style={{
            transform: `perspective(1000px) rotateX(${
              mousePosition.y * -5
            }deg) rotateY(${mousePosition.x * 5}deg)`,
            transition: "transform 0.3s ease-out",
          }}
        >
          <div
            className={`transition-all duration-1000 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-12"
            }`}
          >
            {heroSection?.badge && (
              <div
                className={`inline-block px-6 py-3 bg-white/10 border border-white/30 rounded-full mb-8 backdrop-blur-md shadow-2xl animate-fade-slide-down`}
                style={{ animationDelay: "0.2s" }}
              >
                <span className="text-white text-sm font-semibold tracking-wide">
                  {heroSection.badge.text}
                </span>
              </div>
            )}

            <h1 className="text-6xl md:text-8xl font-extrabold text-white mb-6 leading-tight drop-shadow-2xl animate-fade-slide-up">
              {heroSection?.heading ? (
                <span
                  dangerouslySetInnerHTML={{ __html: heroSection.heading }}
                  className="animate-gradient-x"
                />
              ) : (
                <>
                  <span
                    className="inline-block animate-fade-slide-up"
                    style={{ animationDelay: "0.3s" }}
                  >
                    Chuyển đổi số
                  </span>
                  <br />
                  <span
                    className="inline-block text-[#00c897] drop-shadow-[0_0_30px_rgba(0,200,151,0.5)] animate-gradient-x"
                    style={{ animationDelay: "0.5s" }}
                  >
                    Thành công vượt trội
                  </span>
                </>
              )}
            </h1>

            <p
              className="text-xl md:text-2xl text-gray-100 max-w-4xl mx-auto mb-12 leading-relaxed drop-shadow-lg animate-fade-in"
              style={{ animationDelay: "0.7s" }}
            >
              {heroSection?.description ||
                "Từ giải pháp CRM toàn diện đến dịch vụ phát triển phần mềm, bảo mật và tích hợp AI. Chúng tôi đồng hành cùng doanh nghiệp trên hành trình chuyển đổi số."}
            </p>

            <div
              className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in"
              style={{ animationDelay: "0.9s" }}
            >
              {heroSection?.buttons && heroSection.buttons.length > 0 ? (
                heroSection.buttons
                  .filter((button: any) => button.isVisible !== false)
                  .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
                  .map((button: any, index: number) => {
                    const buttonStyle = button.style || "primary";
                    const buttonClasses =
                      buttonStyle === "primary"
                        ? "bg-[#00c897] text-white hover:bg-[#00b587] hover:shadow-[0_20px_60px_rgba(0,200,151,0.5)] shadow-lg"
                        : buttonStyle === "outline"
                        ? "border-2 border-white/80 text-white hover:bg-white/20 hover:border-white backdrop-blur-md shadow-lg"
                        : "bg-white/10 text-white border-2 border-white/30 hover:bg-white/20 hover:border-white/50 backdrop-blur-md";

                    if (button.actionType === "scroll") {
                      return (
                        <a
                          key={index}
                          href={button.url || "#"}
                          className={`px-8 py-3.5 text-base font-bold rounded-xl transition-all duration-300 whitespace-nowrap hover:scale-105 hover:-translate-y-1 flex items-center justify-center gap-2.5 group ${buttonClasses}`}
                          style={{ transform: "translateZ(50px)" }}
                        >
                          <i className="ri-arrow-down-line text-xl group-hover:translate-y-1 transition-transform duration-300"></i>
                          {button.text}
                        </a>
                      );
                    }

                    return (
                      <Link
                        key={index}
                        to={button.url || "#"}
                        className={`px-8 py-3.5 text-base font-bold rounded-xl transition-all duration-300 whitespace-nowrap hover:scale-105 hover:-translate-y-1 flex items-center justify-center gap-2.5 group ${buttonClasses}`}
                        style={{ transform: "translateZ(50px)" }}
                      >
                        <i className="ri-calendar-line text-xl group-hover:rotate-12 transition-transform duration-300"></i>
                        {button.text}
                      </Link>
                    );
                  })
              ) : (
                <>
                  <Link
                    to="/lien-he"
                    className="px-8 py-3.5 bg-[#00c897] text-white text-base font-bold rounded-xl hover:bg-[#00b587] transition-all duration-300 whitespace-nowrap shadow-lg hover:shadow-[0_20px_60px_rgba(0,200,151,0.5)] hover:scale-105 hover:-translate-y-1 flex items-center justify-center gap-2.5 group"
                    style={{ transform: "translateZ(50px)" }}
                  >
                    <i className="ri-calendar-line text-xl group-hover:rotate-12 transition-transform duration-300"></i>
                    Tư vấn miễn phí
                  </Link>
                  <a
                    href="#solutions"
                    className="px-8 py-3.5 border-2 border-white/80 text-white text-base font-bold rounded-xl hover:bg-white/20 hover:border-white transition-all duration-300 whitespace-nowrap backdrop-blur-md hover:scale-105 hover:-translate-y-1 flex items-center justify-center gap-2.5 group shadow-lg"
                    style={{ transform: "translateZ(50px)" }}
                  >
                    <i className="ri-arrow-down-line text-xl group-hover:translate-y-1 transition-transform duration-300"></i>
                    Khám phá ngay
                  </a>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 animate-bounce z-20">
          <div className="w-8 h-12 border-2 border-white/40 rounded-full flex justify-center backdrop-blur-sm">
            <div className="w-1.5 h-4 bg-white/70 rounded-full mt-2 animate-scroll"></div>
          </div>
        </div>
      </section>

      {/* Solutions Section with 3D Cards */}
      <section
        ref={solutionsRef}
        data-section="solutions"
        id="solutions"
        className="py-32 bg-white relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-[#00c897]/5 rounded-full blur-3xl"></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div
            className={`text-center mb-20 transition-all duration-1000 ${
              visibleSections.solutions
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-12"
            }`}
          >
            <div className="inline-block px-4 py-2 bg-[#2a4b7c]/10 rounded-full mb-4 animate-bounce-subtle">
              <span className="text-[#2a4b7c] text-xs font-bold uppercase tracking-widest">
                ✨ Giải pháp
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold mb-4 py-2 leading-tight bg-clip-text text-transparent bg-gradient-to-r from-[#2a4b7c] via-[#1e3a5f] to-[#00c897] animate-gradient-x">
              Nền tảng công nghệ toàn diện
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Giải pháp chuyển đổi số được thiết kế riêng cho doanh nghiệp Việt
              Nam
            </p>
          </div>

          <div className="space-y-40" style={{ perspective: "2000px" }}>
            {loading ? (
              <div className="text-center py-20">
                <div className="inline-block w-16 h-16 border-4 border-[#2a4b7c] border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-gray-600">Đang tải...</p>
              </div>
            ) : solutions.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-gray-600 text-lg">Chưa có giải pháp nào</p>
              </div>
            ) : (
              solutions.map((solution, index) => (
                <div
                  key={index}
                  className={`grid lg:grid-cols-2 gap-20 items-center ${
                    index % 2 === 1 ? "lg:flex-row-reverse" : ""
                  }`}
                  style={{
                    transformStyle: "preserve-3d",
                  }}
                >
                  {/* Content */}
                  <div
                    className={`transition-all duration-1000 ${
                      visibleSections.solutions
                        ? "opacity-100 translate-x-0"
                        : "opacity-0 -translate-x-20"
                    } ${index % 2 === 1 ? "lg:order-2" : ""}`}
                    style={{
                      transitionDelay: `${index * 200}ms`,
                    }}
                  >
                    <div
                      className={`inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r ${solution.gradient} rounded-xl mb-6 shadow-lg hover:scale-105 hover:shadow-xl transition-all duration-500`}
                      style={{
                        transform: "translateZ(30px)",
                      }}
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-lg">
                        <i
                          className={`${solution.icon} text-2xl text-white`}
                        ></i>
                      </div>
                      <span className="text-white font-bold text-base">
                        {solution.title}
                      </span>
                    </div>

                    <h3 className="text-2xl md:text-3xl font-bold text-[#2a4b7c] mb-4 leading-snug">
                      {solution.subtitle}
                    </h3>
                    <p className="text-base md:text-lg text-gray-700 font-semibold mb-3">
                      {solution.description}
                    </p>
                    <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-8">
                      {solution.fullDescription}
                    </p>

                    {/* Features Grid */}
                    <div className="grid grid-cols-2 gap-3 mb-8">
                      {solution.features.map((feature: string, idx: number) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2.5 p-2.5 bg-gray-50 rounded-lg hover:bg-[#00c897]/10 hover:scale-105 transition-all duration-500"
                          style={{
                            transform: "translateZ(20px)",
                          }}
                        >
                          <div className="w-6 h-6 flex items-center justify-center bg-[#00c897] rounded-full flex-shrink-0">
                            <i className="ri-check-line text-xs text-white"></i>
                          </div>
                          <span className="text-xs md:text-sm text-gray-700 font-medium">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>

                    <Link
                      to={solution.link}
                      className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#2a4b7c] text-white text-base font-bold rounded-xl hover:bg-[#1e3a5f] transition-all duration-500 whitespace-nowrap shadow-lg hover:shadow-xl hover:scale-105 hover:-translate-y-1 group"
                      style={{
                        transform: "translateZ(40px)",
                      }}
                    >
                      Tìm hiểu thêm
                      <i className="ri-arrow-right-line text-xl group-hover:translate-x-2 transition-transform duration-300"></i>
                    </Link>
                  </div>

                  {/* Image Card with 3D Effect */}
                  <div
                    className={`transition-all duration-1000 ${
                      visibleSections.solutions
                        ? "opacity-100 translate-x-0"
                        : "opacity-0 translate-x-20"
                    } ${index % 2 === 1 ? "lg:order-1" : ""}`}
                    style={{
                      transitionDelay: `${index * 200 + 100}ms`,
                    }}
                  >
                    <div
                      className="relative group transition-all duration-700"
                      style={{
                        transformStyle: "preserve-3d",
                        transform: "translateZ(0)",
                      }}
                    >
                      <div
                        className={`absolute inset-0 bg-gradient-to-br ${solution.gradient} rounded-2xl blur-3xl opacity-30 group-hover:opacity-50 transition-all duration-700 group-hover:scale-110`}
                      ></div>
                      <div
                        className="relative rounded-2xl overflow-hidden shadow-xl transform group-hover:scale-105 group-hover:rotate-1 transition-all duration-700"
                        style={{
                          height: "450px",
                          transformStyle: "preserve-3d",
                          transform: "translateZ(50px)",
                        }}
                      >
                        {/* Loading Skeleton */}
                        {!loadedImages.has(index) && (
                          <div className="absolute inset-0 bg-gradient-to-br from-gray-200 via-gray-300 to-gray-200 animate-pulse">
                            <div className="absolute inset-0 flex items-center justify-center">
                              <i className="ri-image-line text-6xl text-gray-400"></i>
                            </div>
                          </div>
                        )}

                        <img
                          src={solution.image}
                          alt={solution.title}
                          onLoad={() => handleImageLoad(index)}
                          className={`w-full h-full object-cover group-hover:scale-110 transition-all duration-700 ${
                            loadedImages.has(index)
                              ? "opacity-100"
                              : "opacity-0"
                          }`}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#2a4b7c]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Services Section with 3D Cards */}
      <section
        ref={servicesRef}
        data-section="services"
        className="py-32 bg-gray-100 relative overflow-hidden"
      >
        <div className="absolute bottom-0 left-0 w-[800px] h-[800px] bg-[#2a4b7c]/5 rounded-full blur-3xl"></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div
            className={`text-center mb-20 transition-all duration-1000 ${
              visibleSections.services
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-12"
            }`}
          >
            <div className="inline-block px-4 py-2 bg-[#00c897]/10 rounded-full mb-4 animate-bounce-subtle">
              <span className="text-[#00c897] text-xs font-bold uppercase tracking-widest">
                🚀 Dịch vụ
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold mb-4 text-[#2a4b7c]">
              Dịch vụ phát triển & tối ưu hệ thống doanh nghiệp
            </h2>
            <p className="text-base md:text-lg text-[#2a4b7c] max-w-2xl mx-auto leading-relaxed">
              Từ phát triển phần mềm đến bảo mật và tích hợp AI
            </p>
          </div>

          <div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10"
            style={{ perspective: "2000px" }}
          >
            {loading ? (
              <div className="col-span-2 lg:col-span-3 text-center py-20">
                <div className="inline-block w-16 h-16 border-4 border-[#00c897] border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-gray-600">Đang tải...</p>
              </div>
            ) : services.length === 0 ? (
              <div className="col-span-2 lg:col-span-3 text-center py-20">
                <p className="text-gray-600 text-lg">Chưa có dịch vụ nào</p>
              </div>
            ) : (
              services.map((service, index) => (
                <Link
                  key={index}
                  to={service.link}
                  className={`group relative bg-white rounded-2xl p-8 border-2 border-gray-100 hover:border-[#00c897] transition-all duration-500 hover:shadow-xl overflow-hidden min-w-0 flex flex-col h-full ${
                    index === 3 ? "md:col-span-2 lg:col-span-2" : ""
                  } ${
                    visibleSections.services
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-16"
                  }`}
                  style={{
                    transitionDelay: `${index * 150}ms`,
                    transformStyle: "preserve-3d",
                    transform: "translateZ(0)",
                  }}
                  onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = (e.clientX - rect.left) / rect.width - 0.5;
                    const y = (e.clientY - rect.top) / rect.height - 0.5;
                    e.currentTarget.style.transform = `perspective(1000px) rotateY(${
                      x * 5
                    }deg) rotateX(${-y * 5}deg) translateZ(15px)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform =
                      "perspective(1000px) rotateY(0deg) rotateX(0deg) translateZ(0px)";
                  }}
                >
                  {/* Gradient Background on Hover */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}
                  ></div>

                  {/* Shine Effect */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  </div>

                  {/* Icon */}
                  <div
                    className="relative mb-6"
                    style={{ transform: "translateZ(40px)" }}
                  >
                    <div
                      className={`w-16 h-16 flex items-center justify-center bg-gradient-to-br ${service.color} rounded-xl shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}
                    >
                      <i className={`${service.icon} text-3xl text-white`}></i>
                    </div>
                  </div>

                  {/* Content – flex-1 + flex col để "Tìm hiểu thêm" luôn sát đáy */}
                  <div
                    className="relative flex-1 flex flex-col min-h-0"
                    style={{ transform: "translateZ(30px)" }}
                  >
                    {/* Title: đúng 2 dòng – min-height 2 dòng + line-clamp-2 */}
                    <h3 className="text-xl md:text-2xl font-bold text-[#2a4b7c] mb-2 group-hover:text-[#00c897] transition-colors duration-500 flex-shrink-0 min-h-[3.25rem] md:min-h-[3.9rem] line-clamp-2 leading-tight">
                      {service.title}
                    </h3>
                    {service.subtitle && (
                      <p className="text-sm text-[#00c897] font-semibold mb-3 flex-shrink-0">
                        {service.subtitle}
                      </p>
                    )}
                    {/* Mô tả: đúng 4 dòng – min-height 4 dòng + line-clamp-4 */}
                    <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-4 flex-shrink-0 min-h-[5.5rem] md:min-h-[6rem] line-clamp-4">
                      {service.description}
                    </p>

                    {/* Features – min-height để khối feature đều nhau */}
                    <div className="grid grid-cols-2 gap-3 mb-4 min-h-[4.5rem] flex-shrink-0">
                      {service.features.map((feature: string, idx: number) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 group-hover:translate-x-1 transition-transform duration-300"
                          style={{ transitionDelay: `${idx * 50}ms` }}
                        >
                          <i className="ri-check-line text-[#00c897] text-lg flex-shrink-0"></i>
                          <span className="text-xs md:text-sm text-gray-700 font-medium">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Highlight */}
                    {service.highlight && (
                      <div className="p-4 bg-[#00c897]/10 rounded-lg mb-4 group-hover:bg-[#00c897]/20 transition-colors duration-500 flex-shrink-0">
                        <p className="text-xs md:text-sm text-[#2a4b7c] font-bold">
                          {service.highlight}
                        </p>
                      </div>
                    )}

                    {/* Tìm hiểu thêm – mt-auto luôn sát đáy thẻ */}
                    <div className="mt-auto flex items-center gap-2 text-[#2a4b7c] font-bold text-base group-hover:text-[#00c897] transition-colors duration-500 flex-shrink-0 pt-2">
                      <span>Tìm hiểu thêm</span>
                      <i className="ri-arrow-right-line text-xl group-hover:translate-x-2 transition-transform duration-500"></i>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </section>

      <CTASection
        isVisible={visibleSections.cta}
        sectionRef={ctaRef}
        phone={footerData?.footer?.contact?.phone || "1900-xxxx"}
      />

      <style>{`
        @keyframes float-3d {
          0%, 100% { transform: translateY(0) translateZ(0) rotateX(0) rotateY(0); }
          25% { transform: translateY(-20px) translateZ(30px) rotateX(10deg) rotateY(10deg); }
          50% { transform: translateY(-40px) translateZ(60px) rotateX(20deg) rotateY(20deg); }
          75% { transform: translateY(-20px) translateZ(30px) rotateX(10deg) rotateY(10deg); }
        }

        @keyframes scroll {
          0% { transform: translateY(0); opacity: 0; }
          50% { opacity: 1; }
          100% { transform: translateY(20px); opacity: 0; }
        }

        @keyframes gradient-x {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        @keyframes fade-slide-up {
          0% { 
            opacity: 0; 
            transform: translateY(30px);
          }
          100% { 
            opacity: 1; 
            transform: translateY(0);
          }
        }

        @keyframes fade-slide-down {
          0% { 
            opacity: 0; 
            transform: translateY(-20px);
          }
          100% { 
            opacity: 1; 
            transform: translateY(0);
          }
        }

        @keyframes fade-in {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }

        @keyframes bounce-subtle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }

        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 3s ease infinite;
        }

        .animate-fade-slide-up {
          animation: fade-slide-up 0.8s ease-out forwards;
          opacity: 0;
        }

        .animate-fade-slide-down {
          animation: fade-slide-down 0.8s ease-out forwards;
          opacity: 0;
        }

        .animate-fade-in {
          animation: fade-in 1s ease-out forwards;
          opacity: 0;
        }

        .animate-bounce-subtle {
          animation: bounce-subtle 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
